import { CacheProvider } from '@emotion/react';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { useEffect, useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { useDirection } from '../shared/hooks/use-direction';
import { ColorSchemeProvider, useColorScheme } from './color-scheme';
import { createAppTheme } from './index';
import { createEmotionCache } from './rtl-cache';

// Cleanup for the now-removed Density experiment: when that provider ran it
// pinned `document.documentElement.style.fontSize` to 14/18px. After the
// provider was deleted the inline style was left behind for sessions that had
// it set, shrinking every rem-based size in the app. Runs once on module
// import — covers both fresh loads and HMR. Safe no-op when nothing is set.
if (typeof document !== 'undefined') {
  if (document.documentElement.style.fontSize) {
    document.documentElement.style.fontSize = '';
  }
  try {
    window.localStorage.removeItem('sahib-preferences');
  } catch {
    // localStorage may be unavailable (private mode, quota) — ignore.
  }
}

interface ThemeProviderProps {
  children: ReactNode;
}

// Outer wrapper mounts ColorSchemeProvider so every descendant — including the
// inner that builds the MUI theme, AND any toggle button anywhere in the app —
// reads the same color-mode state.
export function ThemeProvider({ children }: ThemeProviderProps) {
  return (
    <ColorSchemeProvider>
      <ThemeProviderInner>{children}</ThemeProviderInner>
    </ColorSchemeProvider>
  );
}

function ThemeProviderInner({ children }: ThemeProviderProps) {
  const { i18n } = useTranslation();
  const direction = useDirection();
  const { mode } = useColorScheme();

  const cache = useMemo(() => createEmotionCache(direction), [direction]);
  const theme = useMemo(() => createAppTheme(mode, direction), [mode, direction]);

  useEffect(() => {
    document.documentElement.setAttribute('dir', direction);
    document.documentElement.setAttribute('lang', i18n.language);
  }, [direction, i18n.language]);

  return (
    <CacheProvider value={cache}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </CacheProvider>
  );
}
